import { useState, useEffect } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckSquare, Lock, Crown } from "lucide-react";
import { CHECKLIST_ITEMS } from "@/data/staticData";
import { toast } from "sonner";

const catColors = { "RGPD": "bg-blue-100 text-blue-700", "AI Act": "bg-amber-100 text-amber-700", "Pratiques": "bg-green-100 text-green-700" };

export default function ChecklistPage() {
  const { user } = useAuth();
  const [checked, setChecked] = useState({});
  const [filter, setFilter] = useState("all");
  const isPremium = user?.role === "premium" || user?.role === "admin";

  useEffect(() => { if (isPremium) loadProgress(); }, [isPremium]);

  const loadProgress = async () => {
    try {
      const { data } = await API.get("/checklist/progress");
      const map = {};
      data.forEach(d => { map[d.item_id] = d.checked; });
      setChecked(map);
    } catch {}
  };

  const toggleItem = async (itemId) => {
    if (!isPremium) { toast.info("Fonctionnalité Premium : passez en Premium pour sauvegarder."); return; }
    const newVal = !checked[itemId];
    setChecked(prev => ({ ...prev, [itemId]: newVal }));
    try {
      await API.post("/checklist/progress", { item_id: itemId, checked: newVal });
    } catch {
      setChecked(prev => ({ ...prev, [itemId]: !newVal }));
    }
  };

  const filtered = filter === "all" ? CHECKLIST_ITEMS : CHECKLIST_ITEMS.filter(i => i.category === filter);
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const progress = Math.round((checkedCount / CHECKLIST_ITEMS.length) * 100);
  const categories = ["all", "RGPD", "AI Act", "Pratiques"];

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="checklist-page">
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <CheckSquare className="h-6 w-6 text-[var(--ia-navy)]" />
          <h1 className="text-2xl font-bold text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Checklist de conformite</h1>
        </div>
        <p className="text-[var(--ia-text-muted)]">16 points essentiels pour une conformité RGPD et AI Act</p>
      </div>

      <Card className="border-[var(--ia-border)] mb-6 animate-fade-in">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progression : {checkedCount}/{CHECKLIST_ITEMS.length}</span>
            <span className="text-sm font-semibold text-[var(--ia-navy)]">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          {!isPremium && (
            <div className="flex items-center gap-2 mt-3 text-xs text-amber-700 bg-amber-50 p-2 rounded-md" data-testid="checklist-premium-notice">
              <Lock className="h-3.5 w-3.5" /> <span>La sauvegarde est réservée aux membres Premium</span> <Crown className="h-3 w-3" />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2 mb-4 flex-wrap">
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} data-testid={`filter-${cat}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filter === cat ? "bg-[var(--ia-navy)] text-white" : "bg-white border border-[var(--ia-border)] text-slate-600 hover:bg-slate-50"}`}>
            {cat === "all" ? "Tout" : cat}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map((item, i) => (
          <Card key={item.id} className="border-[var(--ia-border)] animate-fade-in" style={{ animationDelay: `${i * 40}ms` }} data-testid={`checklist-item-${item.id}`}>
            <CardContent className="p-4 flex items-start gap-3">
              <Checkbox checked={!!checked[item.id]} onCheckedChange={() => toggleItem(item.id)} className="mt-0.5" data-testid={`check-${item.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${checked[item.id] ? "line-through text-slate-400" : "text-[var(--ia-text)]"}`}>{item.label}</span>
                  <Badge variant="outline" className={`text-[10px] ${catColors[item.category]}`}>{item.category}</Badge>
                </div>
                <p className="text-xs text-[var(--ia-text-muted)] mt-0.5">{item.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
